import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  X, Truck, Loader2, AlertCircle, CheckCircle2, ExternalLink, Printer, Copy, Check,
  RefreshCw, MapPin, DollarSign,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_LABELS = {
  'US-FC': 'USPS First-Class',
  'US-FCP': 'USPS First-Class Package',
  'US-PM': 'USPS Priority Mail',
  'US-PMI': 'USPS Priority Mail International',
  'US-EMI': 'USPS Priority Mail Express International',
  'US-EM': 'USPS Priority Mail Express',
  'US-XM': 'USPS Express Mail',
  'US-MM': 'USPS Media Mail',
  'US-GA': 'USPS Ground Advantage',
};

const PACKAGE_TYPES = [
  'Package', 'Postcard', 'Letter', 'Large Envelope or Flat',
  'Thick Envelope', 'Flat Rate Envelope', 'Flat Rate Padded Envelope',
  'Flat Rate Legal Envelope', 'Small Flat Rate Box', 'Flat Rate Box',
  'Large Flat Rate Box', 'Regional Rate Box A', 'Regional Rate Box B',
];

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(d);
};

const ShippingLabelModal = ({ order, onClose, onLabelCreated }) => {
  const { token } = useAuth();
  const [weight, setWeight] = useState('8');
  const [packageType, setPackageType] = useState('Package');
  const [serviceType, setServiceType] = useState('US-PM');
  const [sampleOnly, setSampleOnly] = useState(false);
  const [memo, setMemo] = useState('');
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [buying, setBuying] = useState(false);
  const [cleansing, setCleansing] = useState(false);
  const [cleansed, setCleansed] = useState(null);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchRates = useCallback(async () => {
    setLoadingRates(true);
    setErr('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/rates`,
        { weight_oz: parseFloat(weight) || 8, package_type: packageType },
        { headers }
      );
      setRates(res.data.rates || []);
      if (res.data.rates?.length) {
        const found = res.data.rates.find((r) => r.service_type === serviceType);
        setSelectedRate(found || res.data.rates[0]);
        if (!found) setServiceType(res.data.rates[0].service_type);
      }
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoadingRates(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weight, packageType, order.id]);

  const cleanseAddress = async () => {
    setCleansing(true);
    setErr('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/cleanse-address`,
        {},
        { headers }
      );
      setCleansed(res.data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setCleansing(false); }
  };

  const buyLabel = async () => {
    setBuying(true);
    setErr('');
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/label`,
        {
          weight_oz: parseFloat(weight) || 8,
          service_type: serviceType,
          package_type: packageType,
          sample_only: sampleOnly,
          memo: memo.trim() || null,
        },
        { headers }
      );
      setSuccess(res.data);
      if (onLabelCreated) onLabelCreated(res.data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBuying(false); }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[70]" data-testid="label-success-modal">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-5 bg-emerald-50 border-b border-emerald-100">
            <h2 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> Label Purchased
            </h2>
            <p className="text-sm text-emerald-700 mt-1">
              {success.service_type ? SERVICE_LABELS[success.service_type] || success.service_type : 'USPS'} - ${success.postage_cost}
            </p>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Tracking number</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200 text-[#1B3A5C] font-mono text-sm" data-testid="tracking-number">
                  {success.shipment?.tracking_number || '-'}
                </code>
                <button onClick={() => navigator.clipboard.writeText(success.shipment?.tracking_number || '')}
                  className="px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 text-sm">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a href={success.label_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#F5A623] text-[#1B3A5C] font-bold text-sm hover:bg-[#D88F1B]"
                data-testid="print-label-btn">
                <Printer className="w-4 h-4" /> Print Label
              </a>
              <a href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${success.shipment?.tracking_number}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-gray-200 text-gray-700 font-bold text-sm hover:bg-gray-50">
                <ExternalLink className="w-4 h-4" /> Track
              </a>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
            <button onClick={onClose} className="px-5 py-2 rounded-lg bg-[#1B3A5C] text-white font-bold text-sm hover:bg-[#0F2A3D]" data-testid="label-close-btn">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]" data-testid="shipping-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-[#1B3A5C]">Create Shipping Label</h2>
            <p className="text-xs text-gray-500">Order #{order.order_number}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {err && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="shipping-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{err}</span>
            </div>
          )}

          {/* Ship-to summary + cleanse */}
          <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700 min-w-0">
                  <p className="font-medium">{order.shipping_address?.name || `${order.user?.first_name || ''} ${order.user?.last_name || ''}`.trim() || '-'}</p>
                  <p className="text-gray-500 text-xs truncate">
                    {[order.shipping_address?.street_address, order.shipping_address?.city, order.shipping_address?.state, order.shipping_address?.zip_code].filter(Boolean).join(', ') || 'No shipping address on file'}
                  </p>
                </div>
              </div>
              <button onClick={cleanseAddress} disabled={cleansing || !order.shipping_address?.zip_code}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-white text-gray-600 disabled:opacity-40 flex-shrink-0"
                data-testid="cleanse-btn">
                {cleansing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify with USPS'}
              </button>
            </div>
            {cleansed && (
              <div className={`mt-3 text-xs ${cleansed.address_match ? 'text-emerald-700' : 'text-amber-700'} flex items-center gap-1.5`}>
                {cleansed.address_match ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {cleansed.address_match ? 'Address verified by USPS' : 'USPS could not match this address - double-check before buying a label'}
                {cleansed.is_po_box && <span className="ml-2 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">PO Box</span>}
              </div>
            )}
          </div>

          {/* Parcel details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Weight (oz)</label>
              <input type="number" min="0.1" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                data-testid="weight-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Package type</label>
              <select value={packageType} onChange={(e) => setPackageType(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
                data-testid="package-type-select">
                {PACKAGE_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Rate shop */}
          <div className="flex items-center justify-between">
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">USPS Service</label>
            <button onClick={fetchRates} disabled={loadingRates || !weight}
              className="text-xs text-[#F5A623] font-bold hover:text-[#D88F1B] disabled:opacity-50 flex items-center gap-1"
              data-testid="get-rates-btn">
              <RefreshCw className={`w-3 h-3 ${loadingRates ? 'animate-spin' : ''}`} />
              {rates.length ? 'Refresh Rates' : 'Get Live Rates'}
            </button>
          </div>

          {rates.length > 0 ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rates.map((r) => {
                const active = serviceType === r.service_type;
                return (
                  <button key={r.service_type} onClick={() => { setServiceType(r.service_type); setSelectedRate(r); }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 text-left transition-all ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`rate-option-${r.service_type}`}>
                    <div>
                      <p className="text-sm font-semibold text-[#1B3A5C]">{SERVICE_LABELS[r.service_type] || r.service_description || r.service_type}</p>
                      <p className="text-xs text-gray-500">{r.delivery_days ? `${r.delivery_days} day${r.delivery_days === '1' ? '' : 's'}` : 'Variable delivery'} - {r.weight_oz} oz</p>
                    </div>
                    <span className="text-base font-bold text-[#F5A623] flex items-center gap-0.5">
                      <DollarSign className="w-3.5 h-3.5" />{r.amount.toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
              data-testid="service-type-select">
              {Object.entries(SERVICE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          )}

          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Memo (optional, prints on label)</label>
            <input type="text" value={memo} onChange={(e) => setMemo(e.target.value)}
              placeholder={`KYIS Order ${order.order_number}`}
              className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30"
              data-testid="memo-input" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-sm text-gray-700">
            <input type="checkbox" checked={sampleOnly} onChange={(e) => setSampleOnly(e.target.checked)}
              className="mt-0.5 accent-[#F5A623]" data-testid="sample-only-checkbox" />
            <span>
              <span className="font-medium">Sample label only</span>
              <span className="text-gray-500 ml-1">(non-billable test - the order will not be marked shipped)</span>
            </span>
          </label>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-gray-500">
            {selectedRate ? <span>Total: <strong className="text-[#1B3A5C]">${selectedRate.amount.toFixed(2)}</strong></span> : 'Choose a service to see the price'}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={buyLabel} disabled={buying || !order.shipping_address?.zip_code}
              className="px-5 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] font-bold text-sm hover:bg-[#D88F1B] disabled:opacity-50 flex items-center gap-2"
              data-testid="buy-label-btn">
              {buying ? <><Loader2 className="w-4 h-4 animate-spin" /> Buying...</> : <><Truck className="w-4 h-4" /> {sampleOnly ? 'Print Sample' : 'Buy Label'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingLabelModal;
